import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { getWithdrawSteps } from "../../constants/steps";
import {
  confirmWithdrawal,
  getWithdrawalById,
  getProductExchangeById,
  CreateWithdrawResponse,
  WithdrawResponse,
  ProductExchangeResponse,
} from "../../../../services/withdrawApiService";
import { useAppSelector } from "../../../../hooks/useAppDispatch";
import { RootState } from "../../../../redux/store";
import { generateGetURL } from "../../../../services/mediaApiService";
import { getProduct, Product } from "../../../../services/productApiService";
import placeholderImage from "../../../../assets/placeholders/picture.jpg";
import BackButton from "../../../../components/BackButton";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
// Import bank accounts service including getBankAccount.
import {
  BankAccount,
  getBankAccount,
} from "../../../../services/bankAccountsService";

const LOCALE = "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep5";

const WithdrawPageStep5: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);
  const navigate = useNavigate();
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);
  const [withdrawalData, setWithdrawalData] = useState<WithdrawResponse | null>(
    null
  );
  const [productExchangeData, setProductExchangeData] =
    useState<ProductExchangeResponse | null>(null);
  const [productDetails, setProductDetails] = useState<Product[]>([]);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  // New state for the full bank account data.
  const [bankAccountData, setBankAccountData] = useState<BankAccount | null>(
    null
  );

  const alertRef = useRef<HTMLDivElement>(null);

  const { profilePicture } = useAppSelector(
    (state: RootState) => state.giftTable
  );

  useEffect(() => {
    const storedWithdrawal = localStorage.getItem("withdrawal");
    if (storedWithdrawal) {
      const parsedData: CreateWithdrawResponse = JSON.parse(storedWithdrawal);
      if (parsedData.withdrawalID) {
        getWithdrawalById(parsedData.withdrawalID)
          .then((data) => {
            setWithdrawalData(data);
          })
          .catch((error) => {
            console.error("Error fetching withdrawal data:", error);
            setAlert({
              message: t("fetchWithdrawalError"),
              level: "error",
            });
          });
      }
      if (parsedData.productExchangeID) {
        getProductExchangeById(parsedData.productExchangeID)
          .then(setProductExchangeData)
          .catch((error) => {
            console.error("Error fetching product exchange data:", error);
            setAlert({
              message: t("fetchProductExchangeError"),
              level: "error",
            });
          });
      }
    } else {
      setAlert({
        message: t("withdrawIDMissingError"),
        level: "error",
      });
    }

    const fetchProfileImage = async () => {
      if (profilePicture) {
        try {
          const media = await generateGetURL(profilePicture);
          setProfileImageUrl(media.signedURL);
        } catch (error) {
          console.error("Error fetching profile picture:", error);
          setProfileImageUrl(null);
        }
      }
    };

    fetchProfileImage();
  }, [profilePicture, t]);

  // Fetch bank account details once we have withdrawalData.
  useEffect(() => {
    if (withdrawalData && withdrawalData.accountNumber) {
      // Here, withdrawalData.accountNumber is an ID.
      getBankAccount(withdrawalData.accountNumber)
        .then((account) => {
          setBankAccountData(account);
        })
        .catch((error) => {
          console.error("Error fetching bank account data:", error);
          setBankAccountData(null);
        });
    }
  }, [withdrawalData]);

  // Updated: Fetch product details based on the new "products" field.
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (productExchangeData) {
        const fetchedProducts = await Promise.all(
          productExchangeData.products.map(async (pq) => {
            try {
              // Get each product by its productId and attach the quantity.
              const prod = await getProduct(pq.productId);
              return { ...prod, quantity: pq.quantity };
            } catch (error) {
              console.error("Error fetching product:", error);
              return null;
            }
          })
        );
        setProductDetails(fetchedProducts.filter(Boolean) as Product[]);
      }
    };

    fetchProductDetails();
  }, [productExchangeData]);

  const handleConfirm = async () => {
    try {
      await confirmWithdrawal({
        withdrawID: withdrawalData?.id,
        productExchangeID: productExchangeData?.id,
      });
      console.log("Withdrawal and product exchange confirmed successfully");
      navigate("/my-table/withdraw/success");
    } catch (error) {
      console.error("Error confirming withdrawal:", error);
      setAlert({
        message: t("confirmWithdrawalError"),
        level: "error",
      });
    }
  };

  const handleBack = () => {
    navigate("/my-table/withdraw/step4");
  };

  return (
    <PageContainer>
      <div className="mt-16"></div>
      <StepIndicator steps={steps} currentStep={4} progressPercentage={85} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text={t("backButtonText")} onClick={handleBack} />
        <h2 className="text-2xl font-bold mb-4 text-primary">
          {t("confirmationTitle")}
        </h2>
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="text-center text-gray-600">
          {t("confirmationDescription")}
        </div>
        {(withdrawalData || productExchangeData) && (
          <div className="flex flex-col items-center mt-6 space-y-4">
            <div className="relative w-24 h-24 rounded-full overflow-hidden">
              <img
                src={profileImageUrl || placeholderImage}
                alt="Event"
                className="w-full h-full object-cover"
              />
            </div>
            {withdrawalData && (
              <div className="text-center">
                <p className="mt-4 font-semibold">{t("destinationAccount")}</p>
                {bankAccountData ? (
                  <p className="text-primary">
                    {bankAccountData.bank} - {bankAccountData.account} (
                    {bankAccountData.clabe})
                  </p>
                ) : (
                  <p className="text-primary">
                    {t("loadingAccountInfo", "Loading account info...")}
                  </p>
                )}
                <p className="mt-4 font-semibold">{t("withdrawAmount")}</p>
                <p className="text-primary text-xl">
                  {withdrawalData.amount.toLocaleString("es-MX", {
                    style: "currency",
                    currency: withdrawalData.currency,
                  })}
                </p>
              </div>
            )}
            {productExchangeData && (
              <div className="text-center mt-8">
                <p className="font-semibold">{t("productExchange")}</p>
                <div className="space-y-4 min-h-[40vh] max-h-[40vh] overflow-y-auto mt-2">
                  {productDetails.map((product) => {
                    // Find the corresponding quantity in productExchangeData.products by matching product.id.
                    const exchangeItem = productExchangeData?.products.find(
                      (item) => item.productId === product.id
                    );
                    return (
                      <div
                        key={product.id}
                        className="flex justify-between items-center p-4 border rounded-lg shadow-md bg-white"
                      >
                        <div className="flex items-center space-x-4">
                          <img
                            src={
                              product.pictures?.[0]?.trim() || placeholderImage
                            }
                            alt={product.title}
                            className="w-24 h-24 object-cover"
                          />
                          <div>
                            <h4 className="font-bold">{product.title}</h4>
                            <p className="text-sm">
                              {product.price.toLocaleString("es-MX", {
                                style: "currency",
                                currency: "MXN",
                              })}
                            </p>
                            {exchangeItem && (
                              <p className="text-sm">
                                {t("quantity", "Quantity")}:{" "}
                                {exchangeItem.quantity}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <Button
          label={t("confirmWithdrawal")}
          onClick={handleConfirm}
          type="primary"
          className="w-full mt-6"
        />
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep5));
